import type { Account } from '@/api/auth';
import { ChatProvider } from './account';

/** Providers with a React adapter. TugiAI (4) is intentionally unsupported. */
export const SUPPORTED_CHAT_PROVIDERS = [
  ChatProvider.Zendesk,
  ChatProvider.LiveChat,
  ChatProvider.Tawk,
  ChatProvider.Comm100,
] as const;

export type SupportedChatProvider = (typeof SUPPORTED_CHAT_PROVIDERS)[number];

export interface ChatConfig {
  provider: SupportedChatProvider;
  key: string;
}

export function isSupportedChatProvider(value: unknown): value is SupportedChatProvider {
  const n = typeof value === 'string' ? Number(value) : value;
  return (
    n === ChatProvider.Zendesk ||
    n === ChatProvider.LiveChat ||
    n === ChatProvider.Tawk ||
    n === ChatProvider.Comm100
  );
}

function coerceChatProvider(value: unknown): SupportedChatProvider | null {
  if (!isSupportedChatProvider(value)) return null;
  return (typeof value === 'string' ? Number(value) : value) as SupportedChatProvider;
}

/** UI gate (mobile Chat tab / NeedHelp). Script load still needs key + supported provider. */
export function isChatEnabled(account: Account | null | undefined): boolean {
  return account?.Chat === true;
}

/**
 * Resolve loadable chat config from account settings.
 * Returns null when Chat is off, ChatKey is empty, or the provider is unsupported (incl. TugiAI).
 */
export function resolveChatConfig(account: Account | null | undefined): ChatConfig | null {
  if (!account || account.Chat !== true) return null;
  const key = typeof account.ChatKey === 'string' ? account.ChatKey.trim() : '';
  if (!key) return null;
  const provider = coerceChatProvider(account.ChatProvider);
  if (provider === null) return null;
  return { provider, key };
}

export interface Comm100KeyParts {
  siteId: string;
  campaignId: string;
}

/**
 * Legacy Comm100 ChatKey shape: `{siteId}-{uuid}`.
 * `siteId` is the first `-` segment; campaign GUID is parts[1]…[5] re-joined.
 */
export function parseComm100Key(chatKey: string): Comm100KeyParts | null {
  const parts = chatKey.split('-');
  if (parts.length < 6) return null;
  const siteId = parts[0]?.trim() ?? '';
  if (!siteId) return null;
  const campaignId = [parts[1], parts[2], parts[3], parts[4], parts[5]].join('-');
  if (!campaignId || campaignId.startsWith('-') || campaignId.endsWith('-')) return null;
  return { siteId, campaignId };
}
